import React, { useRef, useMemo } from 'react';
import { theme } from '../../../theme';
import type { TimeBin } from './KalenderwocheBarChart';
import type { PrimaryAxisVolumeBin } from '../../../services/api';
import { useEChartsLifecycle, INVISIBLE_TOOLTIP_OPTS } from './_chartCommon';
import { useCategoryMeta } from '../../../context/ClassificationSchemaContext';

// <purpose>Quartal.Balken — horizontal Gantt strip with gradient bars + count labels at
// right end. No tooltip popup. Hover-morph: hovering a row dispatches highlight to all
// series → solid fades, K-stack reveals.</purpose>
// <constraints>
//   All bar series silent:true — only updateAxisPointer dispatch drives the morph.
//   yAxis inverse:true → newest quarter at top.
//   One stacked series per cm.categoryKeys entry (gMSB: K0–K4 + the unclassified bucket; VNB: marktprozess) —
//   the reconciliation set, so the stack sums to the bar total.
// </constraints>

type Props = {
  data: TimeBin[];
  rawCategoryData?: PrimaryAxisVolumeBin[];
  filterHash?: string;
};

export const QuartalBalken: React.FC<Props> = ({ data, rawCategoryData, filterHash }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const cm = useCategoryMeta();
  // Stacked-volume series must reconcile to the bar total, and the total (periodVolume, summed from
  // perPrimaryAxisVolumeByPeriod) includes the unclassified bucket — so it is a stack series too. Performance
  // surfaces drop it via performanceCategoryKeys; this is not one.
  const stackKeys = cm.categoryKeys;

  const kPerBin = useMemo(() => {
    const map = new Map<string, Record<string, number>>();
    if (rawCategoryData?.length) {
      for (const b of rawCategoryData) {
        if (!stackKeys.includes(b.primaryAxis)) continue;
        if (!map.has(b.binLabel)) map.set(b.binLabel, {});
        const entry = map.get(b.binLabel)!;
        entry[b.primaryAxis] = (entry[b.primaryAxis] ?? 0) + b.count;
      }
    }
    return map;
  }, [rawCategoryData, stackKeys]);

  useEChartsLifecycle(containerRef, chart => {
    const NAVY = theme.colors.navy;
    const NAVY_DARK = theme.colors.navyDark;
    const NAVY_MID = theme.colors.navyMid;

    const labels = data.map(d => d.label);
    const values = data.map(d => d.count);

    const kSeriesData = stackKeys.reduce<Record<string, number[]>>((acc, k) => {
      acc[k] = data.map(d => kPerBin.get(d.label)?.[k] ?? 0);
      return acc;
    }, {});

    chart.setOption({
      grid: { left: 64, right: 42, top: 16, bottom: 24 },
      tooltip: INVISIBLE_TOOLTIP_OPTS,
      xAxis: {
        type: 'value',
        splitLine: { lineStyle: { color: '#f1f2f6' } },
        axisLabel: { color: '#9095a3', fontSize: 9 },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'category',
        data: labels,
        inverse: true,
        axisLine: { lineStyle: { color: '#dde0e8' } },
        axisTick: { show: false },
        axisLabel: { color: '#9095a3', fontSize: 10 },
      },
      series: [
        {
          name: 'solid',
          type: 'bar',
          silent: true,
          data: values,
          barWidth: '62%',
          itemStyle: {
            color: {
              type: 'linear', x: 0, y: 0, x2: 1, y2: 0,
              colorStops: [
                { offset: 0, color: NAVY_DARK },
                { offset: 1, color: NAVY_MID },
              ],
            },
            borderRadius: [0, 4, 4, 0],
            shadowColor: 'rgba(6,16,90,0.18)',
            shadowBlur: 6,
            shadowOffsetX: 2,
          },
          emphasis: { itemStyle: { opacity: 0 } },
          label: {
            show: true,
            position: 'right',
            color: NAVY,
            fontSize: 10,
            fontWeight: 600,
            formatter: (p: any) => String(values[p.dataIndex] ?? ''),
          },
          z: 1,
        },
        ...stackKeys.map((k, i) => ({
          name: k,
          type: 'bar' as const,
          silent: true,
          stack: 'k',
          ...(i === 0 ? { barGap: '-100%' } : {}),
          barWidth: '62%',
          data: kSeriesData[k],
          itemStyle: {
            color: cm.color(k),
            opacity: 0,
          },
          label: { show: false },
          emphasis: { itemStyle: { opacity: 0.92 } },
          z: 2,
        })),
      ],
    });

    // Hover-morph: axisPointer dispatch on yAxis category index for horizontal bars
    let lastIdx = -1;
    const SERIES_COUNT = 1 + stackKeys.length;
    chart.on('updateAxisPointer', (event: any) => {
      const ya = event.axesInfo?.[0];
      const idx = (ya && ya.value !== undefined && ya.value >= 0) ? ya.value : -1;
      if (idx === lastIdx) return;
      if (lastIdx >= 0) {
        for (let s = 0; s < SERIES_COUNT; s++) {
          chart.dispatchAction({ type: 'downplay', seriesIndex: s, dataIndex: lastIdx });
        }
      }
      if (idx >= 0 && idx < values.length) {
        for (let s = 0; s < SERIES_COUNT; s++) {
          chart.dispatchAction({ type: 'highlight', seriesIndex: s, dataIndex: idx });
        }
      }
      lastIdx = idx;
    });
  }, [data, kPerBin, filterHash, stackKeys, cm.categoryLabelsFromData]);

  return <div ref={containerRef} style={{ width: '100%', height: 280 }} />;
};
